import {
	pxItemTypeGuards as typeGuard,
	isPurgedOrPurging,
	ShareBaseDocument,
	ShareBaseFolder
} from 'phoenix/domain';
import { TrashItem } from 'sharebase/domain';
import { IDeletedItemsService } from 'sharebase/services/deletedItemsService';
import { IDocumentService } from 'sharebase/services/documentService';
import { IFolderService } from 'sharebase/services/folderService';
import { isShareBaseDocument } from 'phoenix/domain/sharebase-document';

/**
 * @ngdoc service
 * @name  sharebase-app.sbaDeletedItemsDataStore
 *
 * @description
 * A datastore used to interact with user's trash.
 *
**/
export class DeletedItemsDataStore {
	public static $inject = [
		'$q',
		'sbDeletedItemsService',
		'sbFolderService',
		'sbDocumentService'
	];

	/**
	 * @ngdoc property
	 * @name sbaDeletedItemsDataStore#deletedItems
	 * @propertyOf sharebase-app.sbaDeletedItemsDataStore
	 * @kind property
	 *
	 * @description
	 * A user's trash content
	 *
	 * @returns {TrashContent[]} An array of documents and folders.
	 */
	public get deletedItems() { 
		return this._trash; 
	}

	private _trash:TrashItem[] = [];

	constructor(
		private $q:ng.IQService,
		private sbDeletedItemsService:IDeletedItemsService,
		private sbFolderService:IFolderService,
		private sbDocumentService:IDocumentService,
	){}

	/**
	 * Find a trash item associated with a document or folder, if any.
	 */
	public getTrash(item:ShareBaseDocument|ShareBaseFolder) {			
		const itemId = isShareBaseDocument(item) ? item.DocumentId : item.FolderId;
		let t:TrashItem;
		for(let i = 0; i < this._trash.length; i++){
			t = this._trash[i];
			if(itemId === t.itemId){
				break;
			}
		}

		if(!t){
			throw new Error('Could not find trash item.');
		}

		return t;
	}

	/**
	 * @ngdoc method
	 * @name sbaDeletedItemsDataStore#load
	 * @methodOf sharebase-app.sbaDeletedItemsDataStore
	 * @kind function
	 *
	 * @description
	 * Loads or refreshes the trash items.
	 *
	 * @returns {Deferred<TrashItem[]>} A promise that will resolve with a list of trash items
	 */
	public load() {
		this._trash = [];

		return this.sbDeletedItemsService
			.getDeletedItems()
			.then(deletedItems => {
				deletedItems = deletedItems.filter(i => !isPurgedOrPurging(i.Embedded.Document || i.Embedded.Folder));
				return this._trash = deletedItems.map(i => new TrashItem(i));
			});
	}

	/**
	 * @ngdoc method
	 * @name sbaDeletedItemsDataStore#restoreItem
	 * @methodOf sharebase-app.sbaDeletedItemsDataStore
	 * @kind function
	 *
	 * @description
	 * Restore an item to its original location.
	 *
	 * @param {TrashContent} item The item to restore, can be either a document or a folder
	 * @returns {Deferred<ShareBaseItem>} A promise that will resolve to the object that was restored.
	 */
	public restoreItem(trash:TrashItem) {
		return this.sbDeletedItemsService.restoreItem(trash.toApi()).then(() => {
			this.removeItem(trash);
			return trash;
		});
	}

	/**
	 * @ngdoc method
	 * @name sbaDeletedItemsDataStore#purgeItem
	 * @methodOf sharebase-app.sbaDeletedItemsDataStore
	 * @kind function
	 * 
	 * @description
	 * Permanently delete an item from the trash.
	 * 
	 * @param {TrashItem} item The item to purge. Can be either a document or a folder.
	 * @returns {Deferred} A promise that resolves to an HTTP response.
	 */		
	public purgeItem(trash:TrashItem) {
		let p:ng.IPromise<any>;
		let item = trash.item;
		if (typeGuard.isShareBaseDocument(item)) {
			p = this.sbDocumentService.deleteDocument(item, true);
		}
		else {
			p = this.sbFolderService.deleteFolder(item, true);
		}

		return p.then(() => {
			this.removeItem(trash);	
			return trash;
		});
	}

	/**
	 * @ngdoc method
	 * @name sbaDeletedItemsDataStore#purgeAll
	 * @methodOf sharebase-app.sbaDeletedItemsDataStore
	 * @kind function
	 *
	 * @description
	 * Permanently all provided items
	 *
	 * @param {TrashItem[]} items The collection of items to purge
	 * @returns {Deferred} A promise that resolves to an HTTP response.
	 */
	public purgeAll(items:TrashItem[]) {
		let promises = items.map(i => this.purgeItem(i)); 
		return this.$q.all(promises);
	}

	/**
	 * @ngdoc method
	 * @name sbaDeletedItemsDataStore#restoreAll
	 * @methodOf sharebase-app.sbaDeletedItemsDataStore
	 * @kind function
	 *
	 * @description
	 * Restore all provided items to their original location
	 *
	 * @param {TrashContent[]} items The items to restore
	 * @returns {Deferred} A promise that resolves when all items have been restored.
	 */
	public restoreAll(items:TrashItem[]) {
		let promises = items.map(i => this.restoreItem(i)); 
		return this.$q.all(promises);
	}

	/**
	 * @ngdoc method
	 * @name sbaDeletedItemsDataStore#sort
	 * @methodOf sharebase-app.sbaDeletedItemsDataStore
	 * @kind function
	 * 
	 * @description
	 * Sort trash content based on provided sort parameters
	 * 
	 * @param {Object} sortType Parameters that determine how to sort the data
	 */


	private removeItem(trash:TrashItem){
		let removeIndex = this._trash.indexOf(trash);
		this._trash.splice(removeIndex, 1);
	}
}


// WEBPACK FOOTER //
// ./src/sharebase-app/dataStores/deletedItemsDataStore.ts